import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const objectOptions = [
  { label: 'Assistência / Orientação Técnica', value: 'assistencia_orientacao_tecnica' },
  { label: 'Assistência Técnica e Extensão Rural - ATER', value: 'ater' },
  { label: 'Horas-trator / Máquina (Com custo)', value: 'horas_trator_maquina_com_custo' },
  { label: 'Horas-trator / Máquina (Gratuitamente)', value: 'horas_trator_maquina_gratuitamente' },
  { label: 'Fornecimento de Insumos (Calcário, fertilizantes, sementes, mudas)', value: 'fornecimento_insumos' },
  { label: 'Apoio à elaboração de projetos de crédito rural', value: 'projetos_credito_rural' },
  { label: 'Realização de eventos (Dias de campo, cursos, seminários)', value: 'eventos' },
  { label: 'Associativismo e Cooperativismo', value: 'associativismo_cooperativismo' },
  { label: 'Acesso à água para consumo', value: 'acesso_agua_consumo' },
  { label: 'Irrigação', value: 'irrigacao' },
  { label: 'Avicultura', value: 'cadeia_avicultura' },
  { label: 'Apicultura', value: 'cadeia_apicultura' },
  { label: 'Orizicultura (Arroz na agricultura familiar)', value: 'cadeia_arroz' },
  { label: 'Cacauicultura', value: 'cadeia_cacauicultura' },
  { label: 'Cafeicultura', value: 'cadeia_cafeicultura' },
  { label: 'Floricultura', value: 'cadeia_floricultura' },
  { label: 'Fruticultura', value: 'cadeia_fruticultura' },
  { label: 'Mandiocultura', value: 'cadeia_mandioca' },
  { label: 'Olericultura', value: 'cadeia_olericultura' },
  { label: 'Pecuária de Leite', value: 'cadeia_pecuaria_leite' },
  { label: 'Pecuária de Corte', value: 'cadeia_pecuaria_corte' },
  { label: 'Piscicultura', value: 'cadeia_piscicultura' },
  { label: 'Sistemas Agroflorestais (SAFs)', value: 'cadeia_safs' },
  { label: 'Plantas Medicinais, Aromáticas e Condimentares', value: 'cadeia_plantas_medicinais' },
  { label: 'Produção Agroecológica', value: 'producao_agroecologica' },
  { label: 'Produção Orgânica', value: 'producao_organica' },
  { label: 'Serviço de Inspeção Municipal - SIM', value: 'sim' },
  { label: 'Apoio à realização de feiras (Livres e/ou Permanentes)', value: 'apoio_feiras' },
  { label: 'Apoio ao PNAE (Merenda Escolar)', value: 'projetos_pnae' },
  { label: 'Apoio ao PAA (Aquisição de Alimentos Federal)', value: 'propostas_paa' },
  { label: 'Programa Municipal de Aquisição de Alimentos (Recursos Próprios)', value: 'programa_aquisicao_alimentos' },
  { label: 'Compras Governamentais Municipais', value: 'compras_governamentais' },
  { label: 'Comercialização em Canais Convencionais (Mercados, Hotéis)', value: 'comercializacao_convencional' },
  { label: 'Comercialização Eletrônica (Sites, Apps)', value: 'comercializacao_eletronica' },
  { label: 'Apoio na emissão de Nota Fiscal', value: 'cadastro_nota_fiscal' },
  { label: 'Declaração de Produção para Inscrição Estadual', value: 'declaracao_producao_ie' },
  { label: 'Ações para Juventude Rural', value: 'juventude_rural' },
  { label: 'Ações para Mulheres Rurais', value: 'mulheres_rurais' },
  { label: 'Ações para Povos Indígenas', value: 'indigenas' },
  { label: 'Ações para Quilombolas', value: 'quilombolas' },
  { label: 'Ações para Outros Povos e Comunidades', value: 'pct' },
  { label: 'Artesanato Rural', value: 'artesanato' },
  { label: 'Geoprocessamento', value: 'geoprocessamento' },
  { label: 'Licenciamento Ambiental', value: 'licenciamento_ambiental' },
  { label: 'Cadastro Ambiental Rural - CAR', value: 'car' },
  { label: 'Preservação Ambiental', value: 'preservacao_ambiental' },
  { label: 'Extrativismo e Sociobiodiversidade', value: 'extrativismo' },
  { label: 'Manejo Ambiental', value: 'manejo_ambiental' },
  { label: 'Recuperação de Áreas Degradadas', value: 'recuperacao_ambiental' },
  { label: 'Economia Solidária', value: 'economia_solidaria' },
  { label: 'Segurança Alimentar e Nutricional', value: 'seguranca_alimentar' },
  { label: 'Outro', value: 'other' },
];

export interface Demand {
  object: string;
  quantity: number;
  justification: string;
  year: number;
}

export interface Step5CmdrsData {
  demands: Demand[];
}

interface Step5Props {
  hasCmdrs: boolean | null;
  disabled?: boolean;
  onNext: () => void;
  onBack: () => void;
  onChange: (data: Step5CmdrsData) => void;
  data: Step5CmdrsData;
}

const Step5: React.FC<Step5Props> = ({ hasCmdrs, disabled, onNext, onBack, onChange, data }) => {
  const currentYear = new Date().getFullYear();
  const [demands, setDemands] = useState<Demand[]>(data?.demands || []);
  const [openDialog, setOpenDialog] = useState(false);
  const [objectSelect, setObjectSelect] = useState<string>('');
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [form, setForm] = useState<Demand>({
    object: '',
    quantity: 1,
    justification: '',
    year: currentYear,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    onChange({ demands });
    // eslint-disable-next-line
  }, [demands]);

  useEffect(() => {
    setDemands(data?.demands || []);
  }, [data?.demands]);


  const handleOpenDialog = (idx?: number) => {
    if (typeof idx === 'number') {
      const d = demands[idx];
      setForm({ ...d });
      const isPreset = objectOptions.some(o => o.value === d.object);
      setObjectSelect(isPreset ? d.object : 'other');
      setEditIdx(idx);
    } else {
      setForm({ object: '', quantity: 1, justification: '', year: currentYear });
      setObjectSelect('');
      setEditIdx(null);
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditIdx(null);
  };

  const handleFormChange = (field: keyof Demand, value: string | number) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.object) newErrors.object = 'Campo obrigatório';
    if (!form.quantity || isNaN(Number(form.quantity)) || Number(form.quantity) < 1 || !Number.isInteger(Number(form.quantity))) {
      newErrors.quantity = 'Informe um número inteiro positivo';
    }
    if (!form.justification) newErrors.justification = 'Campo obrigatório';
    return newErrors;
  };


  const handleAddOrEditDemand = () => {
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;

    const normalized: Demand = {
      ...form,
      quantity: Number(form.quantity),
      year: form.year,
    };

    if (editIdx !== null) {
      setDemands(prev => prev.map((d, i) => (i === editIdx ? normalized : d)));
    } else {
      setDemands(prev => [...prev, normalized]);
    }

    setOpenDialog(false);
    setEditIdx(null);
  };


  const handleDeleteDemand = (idx: number) => {
    setDemands(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    if (demands.length === 0) {
      setErrors({ submit: 'Adicione pelo menos uma demanda.' });
      return;
    }
    setErrors({});
    onNext();
  };

  let objectValue = '';

  if (objectOptions.some(opt => opt.value === form.object)) {
    objectValue = form.object;
  } else {
    objectValue = form.object === '' ? '' : 'other';
  }



  if (hasCmdrs === false) {
    return (
      <Box>
        <Paper elevation={2} sx={{ p: 3, mb: 4, opacity: 0.6 }}>
          <Typography variant="h6" gutterBottom>
            Demandas
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography color="text.secondary">
            Esta aba está desabilitada pois o município não possui CMDRS.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Demandas
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <TextField
          label="Ano de referência do Questionário"
          value={currentYear}
          InputProps={{ readOnly: true }}
          fullWidth
          margin="normal"
        />

        <Alert
          severity='info'
          sx={{ marginY: '2rem', backgroundColor: '#EAEFF9' }}
        >
          <Typography
            variant='body2'
            fontSize='16px'
            textAlign='left'
            lineHeight={1.5}
          >
            {`Quais são as demandas/necessidades de auxílio em serviços e/ou equipamentos para que esta Secretaria Municipal de Agricultura atenda ao público da agricultura familiar?`}
          </Typography>
        </Alert>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={disabled}
          >
            Adicionar demanda
          </Button>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Objeto</TableCell>
              <TableCell>Quantidade</TableCell>
              <TableCell>Justificativa</TableCell>
              <TableCell>Ano</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {demands.map((demand, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  {objectOptions.find(opt => opt.value === demand.object)?.label ?? demand.object}
                </TableCell>
                <TableCell>{demand.quantity}</TableCell>
                <TableCell>
                  {demand.justification.length > 80
                    ? demand.justification.slice(0, 80) + '...'
                    : demand.justification}
                </TableCell>
                <TableCell>{demand.year}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(idx)} disabled={disabled}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteDemand(idx)} disabled={disabled}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {demands.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhuma demanda adicionada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {errors.submit && (
          <Typography color="error" sx={{ mt: 1 }}>{errors.submit}</Typography>
        )}

        <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onBack}
            disabled={disabled}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={disabled}
          >
            Salvar e continuar
          </Button>
        </Box>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editIdx !== null ? 'Editar Demanda' : 'Adicionar Demanda'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="object-label" shrink>Objeto</InputLabel>
            <Select
              labelId="object-label"
              label="Objeto"
              value={objectSelect}
              onChange={(e) => {
                const v = e.target.value;
                setObjectSelect(v);

                handleFormChange('object', v === 'other' ? (form.object || '') : v);
              }}
              disabled={disabled}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return <span style={{ opacity: 0.6 }}>Selecione…</span>;
                if (selected === 'other') return 'Outro';
                return objectOptions.find(o => o.value === selected)?.label ?? selected;
              }}
            >
              <MenuItem value="" disabled>Selecione…</MenuItem>
              {objectOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
            {errors.object && <Typography color="error">{errors.object}</Typography>}
          </FormControl>


          {objectSelect === 'other' && (
            <TextField
              label="Nome do Objeto"
              value={form.object}
              onChange={(e) => handleFormChange('object', e.target.value.toUpperCase())}
              error={!!errors.object}
              helperText={errors.object}
              fullWidth
              margin="normal"
              disabled={disabled}
              required
              type="text"
            />
          )}

          <TextField
            label="Quantidade"
            type="number"
            value={form.quantity}
            onChange={(e) => handleFormChange('quantity', Number(e.target.value))}
            error={!!errors.quantity}
            helperText={errors.quantity}
            fullWidth
            margin="normal"
            disabled={disabled}
            required
            inputProps={{ min: 1, step: 1 }}
          />
          <TextField
            label="Justificativa"
            value={form.justification}
            onChange={e => {
              if (e.target.value.length <= 500) {
                handleFormChange('justification', e.target.value.toUpperCase());
              }
            }}
            error={!!errors.justification}
            helperText={
              errors.justification
                ? errors.justification
                : `${form.justification.length}/500 caracteres`
            }
            fullWidth
            margin="normal"
            disabled={disabled}
            required
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
          />
          <TextField
            label="Ano de Referência"
            type="number"
            value={form.year}
            onChange={(e) => handleFormChange('year', Number(e.target.value))}
            fullWidth
            margin="normal"
            disabled={disabled}
            inputProps={{ min: 2000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={disabled}>Cancelar</Button>
          <Button onClick={handleAddOrEditDemand} variant="contained" disabled={disabled}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Step5;
